import { navigate } from 'gatsby-link'
import React, { useEffect, useState } from 'react'
import {
  consumeMagicLinkFromLocation,
  refreshLoginSession,
  setLoginSession,
} from '../../utils/authSession'
import { clearReservationDetailCache } from '../../utils/reservationDetailCache'

function prefetchCrmWarmup(profile) {
  if (!profile?.user_id) return
  const scopeId = String(
    profile.accountId || profile.listingId || profile.user_id || '',
  ).trim()

  void import('../../utils/dashboardDiagnosticsPrefetch')
    .then((mod) => {
      if (mod.shouldPrefetchDashboardDiagnostics?.(profile)) {
        return mod.prefetchDashboardDiagnostics?.()
      }
      return undefined
    })
    .catch(() => {})

  void import('../Calendar/reservationSearch')
    .then((mod) =>
      mod.prefetchHotelCalendarReservations?.({ hotelId: scopeId }),
    )
    .catch(() => {})

  void import('../../utils/teamApi')
    .then((mod) => mod.prefetchTeamRoster?.())
    .catch(() => {})
}

function clearCrmWarmupCaches() {
  void import('../../utils/dashboardDiagnosticsPrefetch')
    .then((mod) => mod.clearDashboardDiagnosticsCache?.())
    .catch(() => {})
  void import('../Calendar/reservationSearch')
    .then((mod) => mod.clearHotelCalendarReservationsCache?.())
    .catch(() => {})
  void import('../../utils/teamApi')
    .then((mod) => mod.clearTeamRosterCache?.())
    .catch(() => {})
  clearReservationDetailCache()
}

function decodeJwt(token) {
  if (!token) return null

  const base64Url = token.split('.')[1]
  if (!base64Url) return null
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)

  return JSON.parse(window.atob(padded))
}

function readStoredLoginToken() {
  if (typeof window === 'undefined') return null

  // Prefer a URL magic link over any leftover localStorage session so invite
  // clicks always land on the invited account.
  const params = new URLSearchParams(window.location.search)
  const urlToken = String(params.get('doggos_l') || '').trim()
  if (urlToken) {
    setLoginSession(urlToken, { forceNotify: true })
    return urlToken
  }

  return window.localStorage.getItem('doggos_l')
}

function profileFromToken(token, withSession) {
  if (!token) return null
  try {
    const profile = decodeJwt(token)
    if (withSession) return { profile, token }
    return profile
  } catch {
    return null
  }
}

export const useProfileData = (options = {}) => {
  const withSession = Boolean(options.withSession)
  const optional = Boolean(options.optional)

  const [data, setData] = useState(() => {
    if (typeof window === 'undefined') return null
    return profileFromToken(readStoredLoginToken(), withSession)
  })
  // When a stored session already exists, do not block CRM shell on /api/login
  // refresh — flipping isLoading true→false remounts dashboard children and
  // cleared calendar events (flash). Refresh token in the background instead.
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window === 'undefined') return false
    return !readStoredLoginToken()
  })
  const [error, setError] = useState(null)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    let cancelled = false

    const applyToken = (token) => {
      if (cancelled) return
      const next = profileFromToken(token, withSession)
      setData(next)
      const profile = withSession ? next?.profile : next
      prefetchCrmWarmup(profile)
    }

    const fetchData = async () => {
      const hadSession = Boolean(readStoredLoginToken())
      if (!hadSession) setIsLoading(true)
      setError(null)

      try {
        const l = readStoredLoginToken()

        if (l) {
          // Apply JWT claims immediately so avatars don't keep the previous user.
          applyToken(l)
        }

        if (!l) {
          if (!optional) {
            navigate('/', {
              replace: true,
              state: {
                returnTo: window.location.href,
              },
            })
          } else {
            console.debug('Anonymous user')
          }

          if (!cancelled) setData(null)
          return
        }

        const nextToken = await refreshLoginSession()
        if (!cancelled && nextToken) applyToken(nextToken)
      } catch (cause) {
        if (cause?.message === 'login_refresh_failed') {
          if (!optional) {
            window.localStorage.removeItem('doggos_l')
            navigate('/', {
              replace: true,
              state: {
                returnTo: window.location.href,
              },
            })
          }
          if (!cancelled) setData(null)
          return
        }
        if (!cancelled) setError(cause)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchData()

    const onLogout = () => {
      clearCrmWarmupCaches()
      if (!cancelled) setData(null)
    }
    const onLogin = (event) => {
      clearCrmWarmupCaches()
      const token =
        event?.detail?.token || window.localStorage.getItem('doggos_l')
      applyToken(token)
    }
    const onStorage = (event) => {
      if (event.key !== 'doggos_l') return
      if (!event.newValue) {
        onLogout()
        return
      }
      applyToken(event.newValue)
    }

    window.addEventListener('doggos-auth-logout', onLogout)
    window.addEventListener('doggos-auth-login', onLogin)
    window.addEventListener('storage', onStorage)

    return () => {
      cancelled = true
      window.removeEventListener('doggos-auth-logout', onLogout)
      window.removeEventListener('doggos-auth-login', onLogin)
      window.removeEventListener('storage', onStorage)
    }
  }, [withSession, optional])

  if (withSession) {
    return {
      profile: data?.profile ?? null,
      userToken: data?.token ?? null,
      isLoading,
      error,
    }
  }

  return data
}

function AuthProvider(props) {
  useEffect(() => {
    void import('../../utils/dashboardDiagnosticsPrefetch')
      .then((mod) => mod.bootstrapDashboardDiagnosticsPrefetch?.())
      .catch(() => {})
    // Full page loads and soft navigations that remount App.
    consumeMagicLinkFromLocation()
  }, [])

  return props.children
}

export default AuthProvider
